import { proxyFetchToApiRoute } from '@side/next-tools/client-middleware';
import createClient from 'openapi-fetch';
import type {
  AuditSchema,
  CoreSchema,
  IdentitySchema,
  PaymentSchema,
  ProvisionSchema,
} from './generated';

/**
 * openapi-fetch client for interacting with identity-service
 * @see https://docs.internal-services-stage.sideinc.dev/ui/service-calls
 * @see https://openapi-ts.dev/openapi-fetch/
 */
export const identityApi = createClient<IdentitySchema.paths>({
  baseUrl: process.env.IDENTITY_SERVICE_URL,
  // Make sure that datadog modified version of fetch is used so that trace headers are added
  fetch: (...args) => fetch(...args),
});

/**
 * openapi-fetch client for interacting with payment-service
 * @see https://docs.internal-services-stage.sideinc.dev/ui/service-calls
 * @see https://openapi-ts.dev/openapi-fetch/
 */
export const paymentApi = createClient<PaymentSchema.paths>({
  baseUrl: process.env.PAYMENT_SERVICE_URL,
  // Make sure that datadog modified version of fetch is used so that trace headers are added
  fetch: (...args) => fetch(...args),
});

/**
 * openapi-fetch client for interacting with provision-service
 * @see https://docs.internal-services-stage.sideinc.dev/ui/service-calls
 * @see https://openapi-ts.dev/openapi-fetch/
 */
export const provisionApi = createClient<ProvisionSchema.paths>({
  baseUrl: process.env.PROVISION_SERVICE_URL,
  // Make sure that datadog modified version of fetch is used so that trace headers are added
  fetch: (...args) => fetch(...args),
});

/**
 * openapi-fetch client for interacting with core-service
 * @see https://docs.internal-services-stage.sideinc.dev/ui/service-calls
 * @see https://openapi-ts.dev/openapi-fetch/
 */
export const txmApi = createClient<CoreSchema.paths>({
  baseUrl: process.env.TXM_SERVICE_URL,
  // Make sure that datadog modified version of fetch is used so that trace headers are added
  fetch: (...args) => fetch(...args),
});

/**
 * openapi-fetch client for interacting with audit-service
 * @see https://docs.internal-services-stage.sideinc.dev/ui/service-calls
 * @see https://openapi-ts.dev/openapi-fetch/
 */
export const auditApi = createClient<AuditSchema.paths>({
  baseUrl: process.env.AUDIT_SERVICE_URL,
  // Make sure that datadog modified version of fetch is used so that trace headers are added
  fetch: (...args) => fetch(...args),
});

export type ApiError = {
  message?: string | undefined; // i.e. 'Route GET:/team/asdf not found'
  error: string; // i.e 'Not Found'
  statusCode?: number | undefined;
};

export type ProvisionApiError = {
  details: {
    status: number;
  };
  error: {
    code: string;
    message: string;
  };
  type: string; // USERS_ERROR
  method: string; // GET
};

if (typeof window === 'undefined') {
  // Server middleware — require() from a separate subpath so the bundler dead-code
  // eliminates this block (and its crypto dependency) from client bundles.
  const {
    addAuthAndTraceHeaders,
  } = require('@side/next-tools/server-middleware');
  identityApi.use(addAuthAndTraceHeaders);
  txmApi.use(addAuthAndTraceHeaders);
  paymentApi.use(addAuthAndTraceHeaders);
  provisionApi.use(addAuthAndTraceHeaders);
  auditApi.use(addAuthAndTraceHeaders);
} else {
  // Browser middleware
  identityApi.use(proxyFetchToApiRoute('/api/proxies/identity'));
  txmApi.use(proxyFetchToApiRoute('/api/proxies/txm'));
  paymentApi.use(proxyFetchToApiRoute('/api/proxies/payment'));
  provisionApi.use(proxyFetchToApiRoute('/api/proxies/provision'));
  auditApi.use(proxyFetchToApiRoute('/api/proxies/audit'));
}
